<template>
  <div
    class="checkbox-wrapper"
    :class="{ 'pointer-events-none': disabled }"
    @click="toggle">
    <div :style="checkboxStyle" class="checkbox" :class="checkboxCustomClasses">
      <svg
        v-show="isChecked"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        :style="tickStyle"
        class="tick">
        <!-- Using both fill and stroke for a thicker appearance -->
        <path
          :fill="tickColor"
          :stroke="tickColor"
          stroke-width="1"
          d="M9 16.2l-4.2-4.2-1.4 1.4 5.6 5.6 12-12-1.4-1.4z" />
      </svg>
    </div>
    <slot />
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Array, Boolean],
    default: () => false,
  },
  value: {
    type: [String, Number, Object],
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  backgroundColor: {
    type: String,
    default: 'bg-button-primary-default-color1',
  },
  tickColor: {
    type: String,
    default: '#fff',
  },
  size: {
    type: String,
    default: '16px',
  },
  borderColor: {
    type: String,
    default: 'border-line-active-neutral',
  },
  borderRadius: {
    type: String,
    default: '2px',
  },
  borderWidth: {
    type: String,
    default: '1px',
  },
  borderOnChecked: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const isChecked = computed(() => {
  if (Array.isArray(props.modelValue)) {
    if (typeof props.value === 'object') {
      return props.modelValue.some(
        (item) => JSON.stringify(item) === JSON.stringify(props.value),
      );
    }
    return props.modelValue.includes(props.value);
  }
  return props.modelValue;
});

const toggle = (event) => {
  if (props.disabled) {
    event.preventDefault();
    return;
  }
  let updatedValue;
  if (Array.isArray(props.modelValue)) {
    if (isChecked.value) {
      if (typeof props.value === 'object') {
        updatedValue = props.modelValue.filter(
          (item) => JSON.stringify(item) !== JSON.stringify(props.value),
        );
      } else {
        updatedValue = props.modelValue.filter((item) => item !== props.value);
      }
    } else {
      updatedValue = [...props.modelValue, props.value];
    }
  } else {
    updatedValue = !props.modelValue;
  }
  emit('update:modelValue', updatedValue);
};

const checkboxCustomClasses = computed(() => {
  return {
    [props.borderColor]: props.borderColor,
    [props.backgroundColor]: isChecked.value && props.backgroundColor,
  };
});

const checkboxStyle = computed(() => {
  let borderStyle = `${props.borderWidth} solid`;

  if (isChecked.value && !props.borderOnChecked) {
    borderStyle = 'none';
  }

  return {
    width: props.size,
    minWidth: props.size,
    height: props.size,
    borderRadius: props.borderRadius,
    opacity: props.disabled ? '0.7' : '1',
    border: borderStyle,
    cursor: props.disabled ? 'default' : 'pointer',
  };
});

const tickStyle = computed(() => ({
  width: tickSize.value,
  height: tickSize.value,
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
}));

const tickSize = computed(() => {
  const size = parseInt(props.size, 10);
  return size / 1.3 + 'px'; // Adjust tick size here based on your design
});
</script>

<style scoped>
.checkbox-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
  /* You can adjust the gap between the checkbox and slot content */
  cursor: pointer;
}

.checkbox {
  display: inline-block;
  position: relative;
  user-select: none; /* Prevent text selection on double click */
}

.tick {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  line-height: 0;
}
</style>
